import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

const AuditLogsTab = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsOffset, setAuditLogsOffset] = useState(0);
  const [auditLogsLimit] = useState(50);

  const loadAuditLogs = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      const response = await api.get('/admin/audit-logs', {
        params: {
          limit: auditLogsLimit,
          offset: offset
        }
      });
      setAuditLogs(response.data.logs);
      setAuditLogsTotal(response.data.total);
      setAuditLogsOffset(offset);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [auditLogsLimit]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRequestBody = (body) => {
    if (!body) return 'N/A';
    try {
      return JSON.stringify(body, null, 2);
    } catch {
      return String(body);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Audit Logs</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ fontSize: '0.9rem', color: '#718096' }}>
            Total: {auditLogsTotal} | Showing {auditLogsOffset + 1}-{Math.min(auditLogsOffset + auditLogsLimit, auditLogsTotal)}
          </div>
          <button
            className="btn-secondary"
            onClick={() => loadAuditLogs(auditLogsOffset)}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="Refresh audit logs"
          >
            <span>🔄</span>
            Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : auditLogs.length > 0 ? (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Time</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>User</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Method</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Endpoint</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Request Body</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{formatDate(log.created_at)}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{log.username}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: log.method === 'POST' ? '#c6f6d5' : log.method === 'PUT' ? '#fed7aa' : '#fef3c7',
                        color: log.method === 'POST' ? '#22543d' : log.method === 'PUT' ? '#7c2d12' : '#78350f',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}>
                        {log.method}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem', fontFamily: 'monospace' }}>{log.endpoint}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: log.response_status >= 200 && log.response_status < 300 ? '#c6f6d5' : '#fee2e2',
                        color: log.response_status >= 200 && log.response_status < 300 ? '#22543d' : '#991b1b',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}>
                        {log.response_status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.75rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {formatRequestBody(log.request_body).substring(0, 100)}
                        {formatRequestBody(log.request_body).length > 100 ? '...' : ''}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <button
              className="btn-secondary"
              onClick={() => loadAuditLogs(Math.max(0, auditLogsOffset - auditLogsLimit))}
              disabled={auditLogsOffset === 0 || loading}
            >
              Previous
            </button>
            <button
              className="btn-secondary"
              onClick={() => loadAuditLogs(auditLogsOffset + auditLogsLimit)}
              disabled={auditLogsOffset + auditLogsLimit >= auditLogsTotal || loading}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <div className="empty-state">
          <p>No audit logs found.</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogsTab;

